import AddPermit from "../components/AddPermit";
import { useAddPermit } from "../operations/mutations/addPermit";
import { useUpdatePermit } from "../operations/mutations/updatePermit";
import { useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useLazyGetPermitToDig } from "../operations/queries/getPermitToDig";
import { useSubmitPermit } from "../operations/mutations/submitPermit";
import { triggerSavePermitted, triggerSubmitPermitted } from "../models/Permit";
import { PermitDataInput } from "../models/PermitDataInput";
import { useLazyGetAffectedServicesOptions } from "../operations/queries/getAffectedServicesOptions";
import { useGetRegisterAssignmentsByPermitRegisterId } from "../operations/queries/getRegisterAssignmentsByPermitRegisterId";
import { useLazyGetNextPermitNumber } from "../operations/queries/getNextPermitNumber";
import { EsriIntegrationTypes } from "../models/EsriIntegrationType";
import { PermitAssignmentInput } from "../models/PermitAssignmentInput";
import { useLazyGetPermitTypeById } from "../operations/queries/getPermitTypeById";
import { useQueryString } from "../hooks/useQueryString";
import { usePermitRegisterPolicies } from "../hooks/usePermitRegisterPolicies";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import { useAppContext } from "../hooks/useAppContext";
import { Boundary } from "../models/Boundary";
import { SitePhotoInput, PermitFieldValueInput } from "../models/globalTypes";
import { mapBoundaryToAreaInput } from "../utils/boundaryUtils";

const AddPermitContainer = () => {
  const { currentOrganization, currentProject, currentPermitRegister } = useAppContext();
  const { permitId } = useParams<{ permitId: string }>();
  const { sourceId } = useParams<{ sourceId: string }>();
  const queryString = useQueryString();
  const permitTypeId = queryString.get("permitTypeId") ?? undefined;
  const { authorizedToAddDocument } = usePermitRegisterPolicies();

  const { mutate: submitPermitMutate, status: submitPermitStatus } = useSubmitPermit();
  const { getRegisterAssignmentsByPermitRegisterId: getRegisterAssignments, data: registerAssignmentsData } =
    useGetRegisterAssignmentsByPermitRegisterId();
  const { getAffectedServicesOptions, data: affectedServicesOptions } = useLazyGetAffectedServicesOptions();
  const { getPermitToDig: getPermit, data: permit } = useLazyGetPermitToDig();
  const { getPermitToDig: getSourcePermit, data: sourcePermit } = useLazyGetPermitToDig();
  const { getNextPermitNumber, data: getNextPermitNumberData } = useLazyGetNextPermitNumber();
  const { mutate: addPermitMutate, status: addPermitStatus } = useAddPermit();
  const { mutate: updatePermitMutate, status: updatePermitStatus } = useUpdatePermit();
  const { getPermitTypeById, data: permitTypeData } = useLazyGetPermitTypeById();

  const typeId = permit?.type?.id ?? sourcePermit?.type?.id ?? permitTypeId;

  const registerPermitTypeAssignments = useMemo(
    () =>
      registerAssignmentsData?.registerAssignmentsByPermitRegisterId?.filter(
        (ra) => ra.permitTypeOrgRole.permitType.id === typeId,
      ),
    [registerAssignmentsData, typeId],
  );

  useEffect(() => {
    if (typeId && currentOrganization) {
      getPermitTypeById({ variables: { organizationId: currentOrganization.id, permitTypeId: typeId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, currentOrganization]);

  useEffect(() => {
    const permitRegisterId = permit?.permitRegister?.id ?? currentPermitRegister?.id;
    if (permitRegisterId) {
      getRegisterAssignments({ variables: { permitRegisterId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permit, currentPermitRegister]);

  useEffect(() => {
    if (currentPermitRegister && !permitId) {
      getNextPermitNumber({ variables: { permitRegisterId: currentPermitRegister.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPermitRegister, permitId]);

  useEffect(() => {
    getAffectedServicesOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (permitId) {
      getPermit({ variables: { id: permitId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitId]);

  useEffect(() => {
    if (sourceId) {
      getSourcePermit({ variables: { id: sourceId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const handleAddPermit = (
    permitDataInput: PermitDataInput,
    number: number,
    documentIds: string[],
    calibrationCertificate: CalibrationCertificate,
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignmentInputs: PermitAssignmentInput[],
    sitePhotos: SitePhotoInput[],
    fieldValues?: PermitFieldValueInput[],
  ) => {
    if (currentPermitRegister && typeId) {
      addPermitMutate({
        variables: {
          permitDataInput,
          number,
          permitRegisterId: currentPermitRegister.id,
          sourceId: sourcePermit ? sourcePermit.id : "",
          documentIds,
          calibrationCertificate,
          asBuiltRequired,
          areas: boundaries.map((b) => mapBoundaryToAreaInput(b)),
          permitAssignmentInputs,
          sitePhotos,
          permitTypeId: typeId,
          fieldValues,
        },
      });
    }
  };

  const handleEditPermit = (
    id: string,
    permitDataInput: PermitDataInput,
    documentIds: string[],
    calibrationCertificate: CalibrationCertificate,
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignmentInputs: PermitAssignmentInput[],
    sitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => {
    if (currentProject) {
      updatePermitMutate({
        variables: {
          id,
          permitDataInput,
          documentIds,
          calibrationCertificate,
          asBuiltRequired,
          areas: boundaries.map((b) => mapBoundaryToAreaInput(b)),
          permitAssignmentInputs,
          sitePhotos,
          sitePhotoIds,
          fieldValues,
        },
      });
    }
  };

  const handleSubmitPermit = (
    id: string,
    permitDataInput: PermitDataInput,
    number: number,
    documentIds: string[],
    calibrationCertificate: CalibrationCertificate,
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignmentInputs: PermitAssignmentInput[],
    sitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => {
    if (currentPermitRegister && typeId) {
      submitPermitMutate({
        variables: {
          id,
          permitDataInput,
          number,
          permitRegisterId: currentPermitRegister.id,
          sourceId: sourcePermit ? sourcePermit.id : "",
          documentIds,
          calibrationCertificate,
          asBuiltRequired,
          areas: boundaries.map((b) => mapBoundaryToAreaInput(b)),
          permitAssignmentInputs,
          sitePhotos,
          sitePhotoIds,
          permitTypeId: typeId,
          fieldValues,
        },
      });
    }
  };

  return (
    <AddPermit
      organizationHasEsriIntegration={
        currentOrganization?.esriConfig?.integrationTypeId != null &&
        currentOrganization.esriConfig.integrationTypeId !== EsriIntegrationTypes.NONE.id
      }
      saveButtonVisible={!permit || triggerSavePermitted(permit)}
      submitButtonVisible={!permit || triggerSubmitPermitted(permit)}
      permit={permit}
      sourcePermit={sourcePermit}
      currentProject={currentProject}
      affectedServicesOptions={affectedServicesOptions && affectedServicesOptions.affectedServicesOptions}
      registerAssignments={registerPermitTypeAssignments}
      onAddPermit={handleAddPermit}
      addPermitStatus={addPermitStatus}
      onEditPermit={handleEditPermit}
      editPermitStatus={updatePermitStatus}
      onSubmitPermit={handleSubmitPermit}
      submitPermitStatus={submitPermitStatus}
      nextPermitNumber={getNextPermitNumberData?.nextPermitNumber}
      addNewDocumentButtonVisible={authorizedToAddDocument}
      permitType={permitTypeData}
    ></AddPermit>
  );
};

export default AddPermitContainer;
